import {
  Code2, Globe, Smartphone, BrainCircuit, PenTool,
  HeartPulse, GraduationCap, Factory, Store, ShoppingCart, HardHat,
  Building2, Hotel, Plane, Landmark, ShieldCheck, Car, Sprout, Vote,
  Truck, Rocket, Briefcase, Users, UserCheck, RefreshCw, MessagesSquare,
  Clock, Layers, LifeBuoy, Wallet, Headphones, Search, BarChart3,
  DraftingCompass, TestTube2, Wrench, TrendingUp, Linkedin, Twitter,
  Instagram, Facebook
} from "lucide-react";

export const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services", mega: true },
  { label: "Portfolio", path: "/portfolio" },
  { label: "About", path: "/about" },
  { label: "Blog", path: "/blog" },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" },
];

export const SERVICES = [
  {
    id: "custom-software",
    icon: Code2,
    title: "Custom Software Development",
    flagship: true,
    tagline: "Our flagship expertise",
    description: "Bespoke software engineered around your exact workflows — from enterprise ERPs and SaaS products to AI-powered business applications that automate operations, reduce costs, and scale with you.",
    subs: [
      "Enterprise Software Development", "SaaS Product Development", "ERP Development",
      "CRM Development", "HRMS Software", "Inventory Management Systems",
      "Billing & POS Software", "Accounting Software", "Manufacturing ERP",
      "School & College Management Systems", "Hospital & Clinic Management Systems",
      "Logistics Management Software", "Warehouse Management Systems",
      "Hotel Management Systems", "Restaurant Management Software",
      "Construction Management Systems", "Real Estate CRM", "Dealer Management Systems",
      "Franchise Management Systems", "Vendor Management Systems", "Procurement Systems",
      "Booking & Reservation Platforms", "Workflow Automation", "Business Process Automation",
      "AI-Powered Business Applications", "Customer Portals", "Employee Portals",
      "Document Management Systems", "Business Intelligence Dashboards",
      "Reporting & Analytics Systems"
    ],
  },
  {
    id: "web-development",
    icon: Globe,
    title: "Website Development",
    description: "High-performance, conversion-focused websites and web platforms built on modern stacks — designed to rank, load fast, and turn visitors into customers.",
    subs: ["Corporate Websites", "Business Websites", "E-commerce Stores", "WordPress Development", "React Development", "Next.js", "Laravel", "Headless CMS", "Landing Pages", "Website Maintenance"],
  },
  {
    id: "mobile-apps",
    icon: Smartphone,
    title: "Mobile App Development",
    description: "Native and cross-platform mobile apps that users love — from consumer-facing marketplaces to secure enterprise mobility solutions.",
    subs: ["Android", "iOS", "Flutter", "React Native", "Enterprise Mobile Apps", "E-commerce Apps", "Delivery Apps", "Booking Apps"],
  },
  {
    id: "ai-automation",
    icon: BrainCircuit,
    title: "AI & Automation",
    description: "Practical AI that does real work — chatbots, agents, and retrieval-augmented systems grounded in your data, plus intelligent workflow automation.",
    subs: ["AI Chatbots", "OpenAI Integration", "AI Agents", "Workflow Automation", "RAG Applications", "AI Customer Support", "AI Content Solutions", "Intelligent Business Automation"],
  },
  {
    id: "ui-ux",
    icon: PenTool,
    title: "UI/UX Design",
    description: "Research-driven product design that turns complex workflows into intuitive, beautiful experiences your users adopt instantly.",
    subs: ["Product Design", "Wireframes", "Prototypes", "User Research", "Design Systems", "Branding"],
  },
];

export const INDUSTRIES = [
  { icon: HeartPulse, name: "Healthcare" },
  { icon: GraduationCap, name: "Education" },
  { icon: Factory, name: "Manufacturing" },
  { icon: Store, name: "Retail" },
  { icon: ShoppingCart, name: "E-commerce" },
  { icon: HardHat, name: "Construction" },
  { icon: Building2, name: "Real Estate" },
  { icon: Hotel, name: "Hospitality" },
  { icon: Plane, name: "Travel" },
  { icon: Landmark, name: "Finance" },
  { icon: ShieldCheck, name: "Insurance" },
  { icon: Car, name: "Automotive" },
  { icon: Sprout, name: "Agriculture" },
  { icon: Vote, name: "Government" },
  { icon: Truck, name: "Logistics" },
  { icon: Rocket, name: "Startups" },
  { icon: Briefcase, name: "Professional Services" },
];

export const TECH_STACK = [
  { category: "Frontend", items: ["React", "Next.js", "Vue", "Angular"] },
  { category: "Backend", items: ["Node.js", "Laravel", "PHP", "Python", "Java", ".NET"] },
  { category: "Mobile", items: ["Flutter", "React Native", "Swift", "Kotlin"] },
  { category: "Cloud", items: ["AWS", "Azure", "Google Cloud"] },
  { category: "Database", items: ["MySQL", "PostgreSQL", "MongoDB", "Firebase"] },
  { category: "AI", items: ["OpenAI", "LangChain", "Vector Databases"] },
  { category: "CMS", items: ["WordPress", "Shopify", "Webflow"] },
];

export const MARQUEE_ITEMS = [
  "React", "Next.js", "Node.js", "Python", "Flutter", "AWS", "Kubernetes",
  "OpenAI", "PostgreSQL", "MongoDB", "Laravel", "Docker", "LangChain", "Azure", "TypeScript"
];

export const STATS = [
  { value: 250, suffix: "+", label: "Projects Delivered" },
  { value: 120, suffix: "+", label: "Clients Worldwide" },
  { value: 8, suffix: "+", label: "Years of Experience" },
  { value: 15, suffix: "+", label: "Countries Served" },
  { value: 98, suffix: "%", label: "Client Satisfaction" },
];

export const PROCESS_STEPS = [
  { icon: Search, title: "Discovery", description: "We immerse ourselves in your business — goals, users, constraints, and the problem worth solving." },
  { icon: BarChart3, title: "Business Analysis", description: "Requirements become a precise blueprint: workflows, data models, success metrics, and scope." },
  { icon: PenTool, title: "UI/UX Design", description: "Wireframes to polished prototypes, validated with real users before a line of code is written." },
  { icon: DraftingCompass, title: "Architecture Planning", description: "Scalable, secure system design — the right stack, integrations, and infrastructure for your growth." },
  { icon: Code2, title: "Development", description: "Agile sprints with weekly demos. Clean, reviewed, tested code you fully own." },
  { icon: TestTube2, title: "Quality Assurance", description: "Automated and manual testing across devices, loads, and edge cases. Nothing ships broken." },
  { icon: Rocket, title: "Deployment", description: "Zero-downtime releases with CI/CD pipelines, monitoring, and one-click rollbacks." },
  { icon: Wrench, title: "Maintenance", description: "Proactive updates, security patches, and 24×7 support that keeps your system healthy." },
  { icon: TrendingUp, title: "Continuous Improvement", description: "Analytics-driven iteration — your software evolves as your business does." },
];

export const WHY_US = [
  { icon: Users, title: "Experienced Software Engineers", description: "Senior-led teams with deep expertise across web, mobile, AI, and cloud." },
  { icon: UserCheck, title: "Dedicated Project Managers", description: "One accountable point of contact from kickoff to launch and beyond." },
  { icon: RefreshCw, title: "Agile Development", description: "Weekly sprints, live demos, and full visibility into progress at all times." },
  { icon: MessagesSquare, title: "Transparent Communication", description: "Shared dashboards, honest timelines, and no surprises — ever." },
  { icon: Clock, title: "On-time Delivery", description: "Disciplined planning and buffers that keep launches on schedule." },
  { icon: ShieldCheck, title: "Enterprise Security", description: "Secure SDLC, encryption by default, and compliance-ready architecture." },
  { icon: Layers, title: "Scalable Architecture", description: "Systems designed for ten times your current load from day one." },
  { icon: LifeBuoy, title: "Long-term Support", description: "We stay after launch — maintenance, iteration, and growth partnerships." },
  { icon: Wallet, title: "Affordable Global Pricing", description: "World-class engineering at pricing that works for startups and enterprises alike." },
  { icon: Headphones, title: "24×7 Technical Assistance", description: "Round-the-clock support across time zones, whenever you need us." },
];

export const TESTIMONIALS = [
  {
    name: "ComplyIT365",
    role: "Compliance & IT Services",
    industry: "Professional Services",
    rating: 5,
    quote: "Brilliant team and even better responses. I have utilized their services for multiple businesses of mine and they have done a great job making sure each one was set up to my expectation. Thanks a ton Darshan, Ravi & team — let us keep the momentum going.",
    result: "Multiple businesses launched",
  },
  {
    name: "Raju G",
    role: "DBS QA Technologies",
    industry: "Technology",
    rating: 5,
    quote: "Excellent service. An enthusiastic, customer-service-oriented team, always available to connect. I recommend everyone to go ahead without second thoughts. Thank you Darshan and Ravi.",
    result: "Always-available support",
  },
  {
    name: "Moderntechpitstop",
    role: "E-commerce Business",
    industry: "E-commerce",
    rating: 5,
    quote: "They built the best eCommerce website for my business to take it online and running. Now the business is on a growth cycle because of this decision.",
    result: "Business on a growth cycle",
  },
  {
    name: "Gokul Driving School",
    role: "Driving School, Bengaluru",
    industry: "Education",
    rating: 5,
    quote: "The best services for web development in town — on-time delivery of the project and great support after launch. Highly recommend their service.",
    result: "On-time delivery & support",
  },
  {
    name: "Multi Axis",
    role: "E-learning Organization",
    industry: "Education",
    rating: 5,
    quote: "I recently had the pleasure of working with Darshan to create an e-learning website for my organization, and the experience was exceptional. From the initial consultation to the final launch, he demonstrated a deep understanding of the latest technologies and trends in e-learning development.",
    result: "Exceptional launch experience",
  },
  {
    name: "Mary Pinky",
    role: "Enterprise Software Client",
    industry: "Enterprise",
    rating: 5,
    quote: "Serpens provides excellent software that has been a great asset to our company. Their maintenance and support services are top-notch — they always respond quickly and provide the best service. We are very happy with the partnership.",
    result: "Top-notch maintenance",
  },
];

export const JOBS = [
  { id: "sr-fullstack", title: "Senior Full-Stack Engineer", team: "Engineering", location: "Bengaluru / Remote", type: "Full-time", description: "Own features end-to-end across React, Node.js, and cloud infrastructure for enterprise clients." },
  { id: "ai-engineer", title: "AI Solutions Engineer", team: "AI & Automation", location: "Remote", type: "Full-time", description: "Build RAG pipelines, AI agents, and OpenAI integrations that automate real business workflows." },
  { id: "flutter-dev", title: "Flutter Developer", team: "Mobile", location: "Bengaluru", type: "Full-time", description: "Ship beautiful cross-platform apps for logistics, healthcare, and e-commerce products." },
  { id: "uiux-designer", title: "Product Designer (UI/UX)", team: "Design", location: "Hybrid", type: "Full-time", description: "Lead research, wireframes, and design systems for complex enterprise products." },
];

export const FAQS = [
  { q: "How much does custom software development cost?", a: "It depends on scope, but most client projects range from focused modules to full enterprise platforms. After a free discovery call, we provide a fixed, itemized proposal — no hourly black boxes, no surprise invoices." },
  { q: "How long does a typical project take?", a: "An MVP typically ships in 8–12 weeks. Larger platforms like ERPs roll out in phases, so you start getting value from month two instead of waiting for a big-bang launch." },
  { q: "Do you sign NDAs and who owns the code?", a: "Yes — every engagement starts under NDA, and you own 100% of the source code, documentation, and IP from day one." },
  { q: "Can you take over an existing project?", a: "Absolutely. We begin with a code and architecture audit, stabilize the system, then continue development with our agile process and weekly demos." },
  { q: "What happens after launch?", a: "Every project includes a warranty period, and most clients continue with a maintenance plan: monitoring, security patches, improvements, and 24×7 technical assistance." },
  { q: "Which industries do you specialize in?", a: "Healthcare, education, manufacturing, logistics, retail, real estate, and hospitality — with ready domain expertise in ERP, CRM, HRMS, POS, and management systems across 17+ industries." },
];

export const CONTACT_INFO = {
  email: "contact@serpenstech.com",
  phone: "+91 82170 64852",
  phone2: "080 3524 6012",
  whatsapp: "https://wa.me/918217064852",
  address: "India (HQ) — 36, 2nd Main Pipeline Road, Mathikere, Bengaluru 560054",
  mapEmbed: "https://www.google.com/maps?q=Mathikere%2C%20Bengaluru%20560054%2C%20India&output=embed",
};

// Add real profiles as { name, icon, url } entries — footer icons appear automatically
export const SOCIAL_LINKS = [
  { name: "LinkedIn", icon: Linkedin, url: "https://www.linkedin.com/company/serpens-technology-pvt-ltd" },
  { name: "Facebook", icon: Facebook, url: "https://www.facebook.com/people/Serpens-Technology-Pvt-Ltd/100067049412559/" },
  { name: "X", icon: Twitter, url: "https://x.com/Serpens_Tech" },
  { name: "Instagram", icon: Instagram, url: "https://www.instagram.com/invites/contact/?i=lmz6uhgx744a&utm_content=oueu6tl" },
];

export const LEADERSHIP = [
  { name: "Arjun Mehta", role: "Founder & CEO", initials: "AM", bio: "15 years engineering enterprise software across three continents. Believes great software is a business strategy, not an IT expense." },
  { name: "Priya Sharma", role: "Chief Technology Officer", initials: "PS", bio: "Architect of 40+ platforms. Leads our AI practice and keeps our engineering bar uncompromisingly high." },
  { name: "Rahul Verma", role: "Head of Delivery", initials: "RV", bio: "The reason projects ship on time. Runs our agile delivery engine across every client engagement." },
];

export const VALUES = [
  { title: "Ownership", description: "We treat your product like our own company depends on it — because our reputation does." },
  { title: "Craft", description: "Pixel-perfect interfaces, clean architecture, and code we are proud to hand over." },
  { title: "Transparency", description: "Honest estimates, open communication, and dashboards that show real progress." },
  { title: "Long-term Thinking", description: "We build for where your business is going, not just where it is today." },
];
